import { gql, useQuery } from "@apollo/client";
import { useState, useEffect } from "react";
import PaginationUtil from "../common/PaginationUtil";

import Loader from "../common/Loader";

import { AthleteFields, AthleteFieldsLabels } from "./AthleteFields";
import { AthleteValues } from "./AthleteValues";
import { AthleteFilters } from "./AthleteFilters";

const GET_ATHLETES = gql`
  query Query($filter: JSON, $offset: Int, $limit: Int, $sort: [[String]]) {
    athletes(filter: $filter, offset: $offset, limit: $limit, sort: $sort) {
      total
      items {
        id
        lastname
        firstname
        category
        gender
        dob
        department
        scores
        club {
          id
          name
        }
      }
    }
  }
`;

const SORT_COLUMNS = {
  [AthleteFields.score_general]: "scores.general",
  [AthleteFields.score_trail]: "scores.trail",
  [AthleteFields.score_cross]: "scores.cross",
  [AthleteFields.score_off_stadium]: "scores.off_stadium",
};

export default function AthletesTable({
  defaultFilter,
  rows = 100,
  showPagination = true,
  showFilters = true,
  showHeader = true,
  fields = [
    AthleteFields.fullname,
    AthleteFields.club,
    AthleteFields.score_general,
  ],
  responsive = true,
  version,
  useSearchParams = false,
  ...props
}) {
  const [offset, setOffset] = useState(0);
  const [limit, setLimit] = useState(rows);
  const [filter, setFilter] = useState({});
  const [canSearch, setCanSearch] = useState(false);

  const [sortAsc, setSortAsc] = useState(false);
  const [sortField, setSortField] = useState("scores.general");

  useEffect(() => {
    if (filter) {
      if (
        filter.club_id ||
        filter.group_id ||
        filter.athlete_id ||
        filter.ids
      ) {
        setCanSearch(true);
      } else {
        setCanSearch(
          filter.fullname?.length > 3 || filter.club_name?.length > 3
        );
      }
    } else {
      setCanSearch(false);
    }
  }, [setCanSearch, filter]);

  const { data, loading, refetch } = useQuery(GET_ATHLETES, {
    variables: {
      limit: limit,
      offset: offset,
      filter: filter,
      sort: [[sortField, sortAsc ? "ASC" : "DESC"]],
    },
    skip: !canSearch,
  });
  useEffect(() => {
    if (version > 0) {
      refetch();
    }
  }, [version, refetch]);
  useEffect(() => {
    if (defaultFilter) {
      setFilter(defaultFilter);
    }
  }, [defaultFilter]);

  let athletes = JSON.parse(JSON.stringify(data?.athletes?.items ?? []));
  athletes = athletes.map((athlete, index) => {
    athlete._rank = index + 1 + offset;
    return athlete;
  });
  return (
    <div>
      <div className={responsive ? "table-responsive" : "w-100"}>
        <table
          className="table table-striped table-sm"
          style={{ tableLayout: !responsive ? "fixed" : "auto" }}
        >
          {showHeader && (
            <thead>
              <tr>
                {fields.map((field) => (
                  <th
                    key={field}
                    onClick={() => {
                      if (SORT_COLUMNS[field]) {
                        setSortField(SORT_COLUMNS[field]);
                        if (sortField === SORT_COLUMNS[field]) {
                          setSortAsc(!sortAsc);
                        }
                      }
                    }}
                    className={SORT_COLUMNS[field] ? "cursor-pointer" : ""}
                  >
                    {AthleteFieldsLabels[field]}
                    {sortField === SORT_COLUMNS[field] && (
                      <i
                        className={`ps-2 fa ${
                          sortAsc
                            ? "fa-sort-amount-up-alt"
                            : "fa-sort-amount-down-alt"
                        }`}
                      ></i>
                    )}
                  </th>
                ))}
              </tr>
            </thead>
          )}

          {showFilters && (
            <tbody>
              <tr>
                {fields.map((field) => (
                  <td key={field} style={AthleteTdStyles[field]}>
                    <div style={AthleteCellStyles[field]}>
                      <AthleteFilters
                        field={field}
                        filter={filter}
                        setFilter={setFilter}
                        useSearchParams={useSearchParams}
                      />
                    </div>
                  </td>
                ))}
              </tr>
            </tbody>
          )}
          {!canSearch && (
            <tbody>
              <tr>
                <td colSpan={fields.length} className="p-4">
                  <div className="text-center text-muted">
                    Veuillez entrer au moins 3 caractères pour lancer la
                    recherche.
                  </div>
                </td>
              </tr>
            </tbody>
          )}
          {loading && (
            <tbody>
              <tr>
                <td colSpan={fields.length} className="p-4">
                  <Loader />
                </td>
              </tr>
            </tbody>
          )}
          {!loading && athletes.length === 0 && canSearch && (
            <tbody>
              <tr>
                <td colSpan={fields.length} className="p-4">
                  <div className="text-center text-muted">
                    Aucun athlète correspondant.
                  </div>
                </td>
              </tr>
            </tbody>
          )}
          {!loading && canSearch && (
            <tbody>
              {athletes.map((athlete) => (
                <tr key={athlete.id}>
                  {fields.map((field) => (
                    <td
                      key={athlete.id + "-" + field}
                      style={AthleteTdStyles[field]}
                    >
                      <div
                        className="text-nowrap"
                        style={AthleteCellStyles[field]}
                      >
                        <AthleteValues
                          athlete={athlete}
                          field={field}
                          {...props}
                          refetch={refetch}
                        />
                      </div>
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          )}
        </table>
      </div>
      {showPagination && (
        <div>
          {data?.athletes?.total > limit && (
            <PaginationUtil
              showSelectPageSection={true}
              offset={offset}
              limit={limit}
              total={data?.athletes?.total}
              setOffset={setOffset}
              setLimit={setLimit}
            />
          )}
        </div>
      )}
    </div>
  );
}

export const AthleteTdStyles = Object.freeze({
  [AthleteFields.fullname]: {
    width: "100%",
  },
  [AthleteFields.score_general]: {
    width: "120px",
  },
  [AthleteFields.score_trail]: {
    width: "120px",
  },
  [AthleteFields.score_cross]: {
    width: "120px",
  },
  [AthleteFields.score_off_stadium]: {
    width: "120px",
  },
  [AthleteFields.rank]: {
    width: "20px",
  },
});

export const AthleteCellStyles = Object.freeze({
  [AthleteFields.fullname]: {
    width: "100%",
    maxWidth: "300px",
    overflow: "hidden",
    textOverflow: "ellipsis",
    minWidth: "100px",
  },
  [AthleteFields.club]: {
    width: "250px",
    overflow: "hidden",
    textOverflow: "ellipsis",
  },
  [AthleteFields.score_general]: {
    width: "120px",
  },
  [AthleteFields.score_trail]: {
    width: "130px",
  },
  [AthleteFields.score_cross]: {
    width: "130px",
  },
  [AthleteFields.score_off_stadium]: {
    width: "130px",
  },
  [AthleteFields.rank]: {
    width: "20px",
  },
  [AthleteFields.gender]: {
    width: "50px",
  },
  [AthleteFields.category]: {
    width: "80px",
  },
  [AthleteFields.department]: {
    width: "50px",
  },
});
